/**
 * AddNodeModal - Modal for adding new nodes
 *
 * Handles text, image, and URL input
 */

import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Type, Image, Link, Upload, Loader2, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const AddNodeModal = ({ type, position, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Handle image file selection
  const handleImageSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let data = { title };

      // Add additional context if provided
      if (additionalContext.trim()) {
        data.additionalContext = additionalContext.trim();
      }

      if (type === 'text') {
        data.content = content;
      } else if (type === 'url') {
        data.content = url;
        data.sourceUrl = url;
      } else if (type === 'image' && imageFile) {
        const base64 = await fileToBase64(imageFile);
        data.content = 'Image node';
        data.imageBase64 = base64;
        data.mimeType = imageFile.type;
        data.imageUrl = imagePreview; // Temporary preview URL
      }

      await onSubmit(data);
    } catch (error) {
      console.error('Error submitting node:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get icon and title based on type
  const getTypeInfo = () => {
    switch (type) {
      case 'image':
        return { icon: Image, title: 'Add Image', color: 'text-green-400' };
      case 'url':
        return { icon: Link, title: 'Add URL', color: 'text-purple-400' };
      default:
        return { icon: Type, title: 'Add Note', color: 'text-blue-400' };
    }
  };

  const { icon: Icon, title: modalTitle, color } = getTypeInfo();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Icon size={20} className={color} />
            <h2 className="text-lg font-semibold text-white">{modalTitle}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title field (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give it a name..."
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Type-specific input */}
          {type === 'text' && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Content
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your thoughts..."
                rows={5}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                autoFocus
              />
            </div>
          )}

          {type === 'url' && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
                autoFocus
              />
              <p className="mt-1.5 text-xs text-gray-500">
                We'll fetch and summarize the content using AI
              </p>
            </div>
          )}

          {type === 'image' && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Image
              </label>

              {imagePreview ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-gray-900/80 rounded-full hover:bg-gray-900 transition-colors"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 flex flex-col items-center justify-center gap-3 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
                >
                  <Upload size={24} className="text-gray-500" />
                  <span className="text-sm text-gray-400">
                    Click to upload or drag & drop
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 10MB
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                AI Vision will analyze and describe the image
              </p>
            </div>
          )}

          {/* Additional Context (Collapsible) */}
          <div className="border-t border-gray-800 pt-4">
            <button
              type="button"
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              <FileText size={14} />
              <span>Ek Bilgi / Bağlam</span>
              {showContext ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {additionalContext && !showContext && (
                <span className="ml-2 px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs rounded">
                  Eklendi
                </span>
              )}
            </button>

            <AnimatePresence>
              {showContext && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3">
                    <textarea
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="Email özeti, toplantı notları, referans bilgiler..."
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none text-sm"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      Bu alan AI tarafından bağlam olarak kullanılacak ama ana içerikten ayrı tutulacak
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Submit button */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-750 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (type === 'text' && !content) || (type === 'url' && !url) || (type === 'image' && !imageFile)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Add to Map</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddNodeModal;
